'use client'

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useMembership } from "../../hooks/MembershipProvider/MembershipProvider";
import { useApi } from "../../hooks/ApiProvider";

import { giftProcInfo, kwaFormat } from "../../utils/UtilsFunc";
import { useForm } from "react-hook-form";

import { GiftIcons, UserIcons } from "../../utils/IconsSvg";
import "./Order.css";
import useAndroidBridge from "../../hooks/useAndroidBridge";

const Order: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { getProducts } = useApi();
  const { isAuthenticated, isEmbed, isLoading: membershipLoading } = useMembership();
  const { sendToAndroid } = useAndroidBridge();
  const [plan, setPlan] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const [priceType, setPriceType] = useState(`SELF`);
  const {
    register,
    watch,
    getValues,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const phoneNum = watch(`phoneNum`);

  useEffect(() => {
    const planId = searchParams.get("planId");
    const fetchProducts = async (planId: string) => {
      try {
        setIsLoading(true);
        setError(null);
        
        const products = await getProducts();
        
        if (products && products[planId]) {
          setPlan(products[planId]);
        } else {
          setError('상품 정보를 찾을 수 없습니다.');
        }
      } catch (error) {
        console.error('상품 정보 조회 실패:', error);
        if (error instanceof Error) {
          if (error.message.includes('인증 토큰이 없습니다')) {
            setError('로그인이 필요합니다.');
          } else if (error.message.includes('Failed to fetch')) {
            setError('서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.');
          } else {
            setError('상품 정보를 불러오는데 실패했습니다.');
          }
        } else {
          setError('알 수 없는 오류가 발생했습니다.');
        }
      } finally {
        setIsLoading(false);
        setHasFetched(true);
      }
    };

    if (planId && isAuthenticated && !hasFetched && !membershipLoading) {
      fetchProducts(planId);
    }
  }, [searchParams, isAuthenticated, getProducts, hasFetched, membershipLoading]);

  const handlePurchase = async () => {
    // 유효성 검사
    let url = `payment`;
    if (!isAuthenticated) {
      alert(`회원만 구매 가능합니다.`);
      return navigate(`/login`);
    }

    if (priceType === `GIFT`) {
      if (11 > phoneNum?.length) {
        return alert(`잘못된 전화번호 입니다.`);
      }
    }

    const paymentRequest = {
      plan: plan,
      priceType: priceType,
      ...(priceType === "GIFT" ? { phone_number: phoneNum } : {}),
    };

    navigate(url, {
      state: {
        ...paymentRequest,
        ...(isEmbed() ? { embed: true } : {}),
      },
    });
    window.location.reload();
  };

  return (
    <>
      {isEmbed() && (
        <div className={`mobile_app_header`} style={{ background: "#fff" }}>
          <button
            onClick={() => sendToAndroid("go-to-page", "promotion")}
            className={`btn btn_back`}
          ></button>
        </div>
      )}
      <div className={`content_wrap order`}>
        {isLoading && (
          <div className="text-center py-8">
            <p>상품 정보를 불러오는 중...</p>
          </div>
        )}
        
        {error && (
          <div className="text-center py-8 text-red-500">
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              다시 시도
            </button>
          </div>
        )}
        
        {!isLoading && !error && plan?.name && (
          <>
            <div className={`section_wrap sec_wrap_1`}>
              <div className={`sec sec_1`}>
                <div className={`price_box flex items-center justify-between`}>
                  <div className={`body1_sb`}>{plan?.name}</div>
                  <div className={`text-right`}>
                    <p className={`caption`}>
                      <del>{kwaFormat(plan?.originalPrice)}원</del>
                    </p>
                    <p className={`h4_sb`}>{kwaFormat(plan?.price)}원</p>
                  </div>
                </div>
                <div className={`proc_options`}>
                  <h3 className={`h2`}>스토리북을 작성하실 분이 누구인가요?</h3>
                  <div className={`proc_group flex justify-between`}>
                    <button
                      className={`gift ${priceType === `GIFT` ? `on` : ``}`}
                      onClick={() => setPriceType(`GIFT`)}
                    >
                      <GiftIcons width={69} height={46}/>
                      <p className={`body1_sb`}>선물용이에요</p>
                    </button>
                    <button
                      className={`self ${priceType === `SELF` ? `on` : ``}`}
                      onClick={() => setPriceType(`SELF`)}
                    >
                      <UserIcons width={69} height={46}/>
                      <p className={`body1_sb`}>제가 쓸 거예요</p>
                    </button>
                  </div>
                </div>
              </div>
              {priceType === `GIFT` && (
                <div className={`sec sec_2`}>
                  <div className={`form_box`}>
                    <h3 className={`h2`}>
                      받으실 분의 휴대폰 번호를 입력해 주세요.
                    </h3>
                    <div className={`form_group`}>
                      <input
                        {...register("phoneNum", {
                          pattern: {
                            value: /^\d+$/, // ^ $로 전체 매칭
                            message: "전화번호 형식이 아닙니다.",
                          },
                        })}
                        inputMode="numeric"
                        onKeyDown={(e) => {
                          if (!/[0-9]/.test(e.key) && e.key !== "Backspace") {
                            e.preventDefault();
                          }
                        }}
                        maxLength={11}
                        placeholder={`휴대폰 번호 (번호만 입력)`}
                        className={`form_control`}
                        type={`text`}
                      />
                      <p className={`caption`}>
                        쿠폰 코드 및 가입 안내 발송을 위해 정확히 입력해주세요.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className={`sec`}>
                <div className={`warning_message`}>
                  <p>
                    본 상품은 안드로이드 기기에서만 이용할 수 있습니다.<br/>
                    iOS(아이폰) 기기에서는 이용이 불가하니 구매 전 꼭 확인해주세요.
                  </p>
                </div>
              </div>
            </div>
            <div className={`section_wrap sec_wrap_2`}>
              <div className={`sec`}>
                <h3 className={`h4_sb`}>선물용 상품 안내</h3>
                <ul>
                  {/**반복문 구간**/}
                  {giftProcInfo?.map((_val: any, _idx: any) => {
                    return (
                      <li key={_idx}>
                        <p className={`qu_txt caption`}>{_val?.question}</p>
                        <p className={`an_txt caption`}>{_val?.answer}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <div className={`section_wrap sec_wrap_2 ${isEmbed() ? "app" : ""}`}>
              <div className={`sec`}>
                <h3 className={`h4_sb`}>환불 규정</h3>
                <ul className={`caption refund_info`}>
                  <li>
                    <p>
                      환불 금액은 정가가 아닌 실제 결제 금액을 기준으로 계산합니다.
                    </p>
                  </li>
                  <li>
                    <p>이용 기간에 따라 다음과 같이 환불 가능합니다.</p>
                    <nav>
                      <p>이용 기간 7일 이내: 전액 환불</p>
                      <p>
                        이용 기간 1/3 경과 전: 실 결제금액의 2/3에 해당하는 금액
                        환불
                      </p>
                      <p>
                        이용 기간 1/2 경과 전: 실 결제금액의 1/2에 해당하는 금액
                        환불
                      </p>
                      <p>
                        이용 기간 1/2 경과 후: 환불 불가(프리미엄 플랜 한정 실물책
                        신청 전일 경우 실 결제 금액의 1/4에 해당하는 금액 환불)
                      </p>
                    </nav>
                  </li>
                  <li>
                    <p>
                      다음과 같이 서비스를 이용 완료한 경우에는 환불이 불가능합니다.
                    </p>
                    <nav>
                      <p>
                        전자책만 제공하는 상품(베이직 플랜): 환불 신청일과 무관하게
                        '전자책 만들기 신청' 후에는 환불이 불가합니다.
                      </p>
                      <p>
                        전자책 + 실물책 제공 상품(프리미엄, 럭셔리 플랜): 환불
                        신청일과 무관하게 '실물책 인쇄 신청' 후에는 환불이
                        불가합니다.
                      </p>
                    </nav>
                  </li>
                  <li>
                    <p>
                      보다 자세한 환불 규정은 홈페이지 하단 '환불 정책'에서 확인
                      가능합니다.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            <div className={`footer_btn`}>
              <div className={`btn_group`}>
                {priceType === `GIFT` && (
                  <button
                    onClick={handlePurchase}
                    className={`btn ${
                      phoneNum?.length >= 11 ? "" : "dis"
                    } btn-primary block w-[100%]`}
                  >
                    확인
                  </button>
                )}
                {priceType === `SELF` && (
                  <button
                    onClick={handlePurchase}
                    className={`btn btn-primary block w-[100%]`}
                  >
                    확인
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Order;
